import type { BookChapter } from '@/components/books/book';
import type { BookCardBook } from '@/components/books/book-card';
import type { SectionPlacement } from '@/lib/add-ma-section';
import type { MaDeletedSectionRow, MaPlacement, MaSharedBookRef, MaUnplacedSectionRow } from '@/lib/ma-books-api';
import { createContext, useContext } from 'react';

export type BookTocData = {
    book: BookCardBook;
    optimisticChapters: BookChapter[];
    aboutChapter: BookChapter | null;
    contentChapters: BookChapter[];
    unplacedSections: MaUnplacedSectionRow[];
    deletedSections: MaDeletedSectionRow[];
    sharedDocuments: Record<string, MaSharedBookRef[]>;
    showSync: boolean;
    /** TOC create/share/trash/rename/reorder/sync/review — requires seeAllMAContent. */
    canManageToc: boolean;
    dirty: boolean;
    expandedChapterIds: string[];
    setExpandedChapterIds: (ids: string[]) => void;
};

export type BookTocBusy = {
    isBusy: boolean;
    inFlight: boolean;
    trashingSectionId: string | null;
    trashingChapterId: string | null;
    renamingId: string | null;
    excludingId: string | null;
    syncingSectionId: string | null;
};

/** Identity is stable for the lifetime of the provider; handlers read latest state through refs. */
export type BookTocActions = {
    mergeSectionAfterAdd: (
        placement: SectionPlacement,
        section: {
            id: string;
            title: string;
            shortTitle?: string;
            testQuestionDocumentId?: number | null;
            testQuestionDocumentLastEditedAt?: string | null;
            maZybookChapterId?: number;
        },
    ) => void;
    removeUnplacedSection: (maZybookDocumentId: number) => void;
    removeDeletedSection: (maZybookDocumentId: number) => void;
    addDeletedSection: (row: MaDeletedSectionRow) => void;
    mergeSharedDocuments: (maDocumentId: string, books: MaSharedBookRef[]) => void;
    trashSection: (sectionId: string) => Promise<void>;
    trashChapter: (chapterId: string) => Promise<void>;
    renameSection: (sectionId: string, titles: { title: string; shortTitle: string }) => Promise<void>;
    renameChapter: (chapterId: string, titles: { title: string; shortTitle: string }) => Promise<void>;
    syncSection: (sectionId: string) => Promise<void>;
    markSectionSynced: (sectionId: string, syncedAt?: string) => void;
    setSectionReleaseExclusion: (sectionId: string, payload: { placement?: MaPlacement; testBankPlacement?: MaPlacement }) => Promise<void>;
    setChapterReleaseExclusion: (chapterId: string, placement: MaPlacement) => Promise<void>;
    getOptimisticChapters: () => BookChapter[];
    getDragSnapshot: () => BookChapter[] | null;
    beginDragSnapshot: () => void;
    previewSectionDrop: (activeSectionId: string, targetChapterId: string, overIndex: number) => void;
    previewChapterDrop: (activeChapterId: string, overIndex: number) => boolean;
    revertDragSnapshot: () => void;
    clearDragSnapshot: () => void;
    onSectionDrop: (activeSectionId: string, targetChapterId: string, overIndex: number) => void;
    onChapterDrop: (activeChapterId: string, overIndex: number) => void;
    confirmChapterDrop: (dragStartChapterId: string, anchorSectionId: string | null) => void;
};

export const BookTocDataContext = createContext<BookTocData | null>(null);
export const BookTocBusyContext = createContext<BookTocBusy | null>(null);
export const BookTocActionsContext = createContext<BookTocActions | null>(null);

export function useBookTocData(): BookTocData {
    const value = useContext(BookTocDataContext);
    if (!value) {
        throw new Error('useBookTocData must be used within an active BookToc route');
    }
    return value;
}

export function useBookTocBusy(): BookTocBusy {
    const value = useContext(BookTocBusyContext);
    if (!value) {
        throw new Error('useBookTocBusy must be used within an active BookToc route');
    }
    return value;
}

export function useBookTocActions(): BookTocActions {
    const value = useContext(BookTocActionsContext);
    if (!value) {
        throw new Error('useBookTocActions must be used within an active BookToc route');
    }
    return value;
}
